/**
 * AdminMenuGroups — owner cockpit navigatie (Feb 2026 restructure).
 *
 * Vervangt de oude platte lijst van 32 tab-items met 8 duidelijke groepen.
 * Mobiel = accordion-kaarten. Desktop = top-row groepspills met sub-tabs.
 * Alle bestaande tab-ids blijven werken — alleen de navigatie is anders.
 */
import { useState, useMemo } from 'react';
import {
  ShieldCheck, DollarSign, Users, FileText, Cpu, Target, Send, Cog,
  ChevronRight, ChevronDown, Sparkles,
} from 'lucide-react';

/* Vaste mapping. Elk tab-id mag exact 1× voorkomen in de groepen. */
export const MENU_GROUPS = [
  {
    id: 'acties',
    label: 'Acties',
    Icon: ShieldCheck,
    description: 'Vandaag te beslissen — goedkeuren, aanpassen of negeren.',
    tabs: [
      { id: 'control_center', label: 'Control Center' },
      { id: 'approval_inbox', label: 'Approval Inbox' },
      { id: 'tasks',          label: 'Agent Tasks',    advanced: true },
    ],
  },
  {
    id: 'betalingen',
    label: 'Betalingen',
    Icon: DollarSign,
    description: 'Stripe-status, iDEAL, transacties.',
    tabs: [
      { id: 'transactions', label: 'Transacties' },
      { id: 'overview',     label: 'KPI-overzicht' },
    ],
  },
  {
    id: 'klanten',
    label: 'Klanten & leads',
    Icon: Users,
    description: 'Leads, pilots, conversiefunnel.',
    tabs: [
      { id: 'customers',     label: 'Klanten' },
      { id: 'leads',         label: 'Leads' },
      { id: 'ec_leads',      label: 'Energy Control Leads' },
      { id: 'pilot_devices', label: 'Klant-app demo (Pilot Access)' },
      { id: 'funnel',        label: 'Funnel' },
    ],
  },
  {
    id: 'offertes',
    label: 'Offertes',
    Icon: FileText,
    description: 'Aanvragen, voorstellen en installer revenue.',
    tabs: [
      { id: 'quotes',             label: 'Offertes' },
      { id: 'quote_requests',     label: 'Offerteaanvragen' },
      { id: 'installer_revenue',  label: 'Installer Revenue', advanced: true },
    ],
  },
  {
    id: 'apparaten',
    label: 'Producten & apparaten',
    Icon: Cpu,
    description: 'Producten, installateurs, devices.',
    tabs: [
      { id: 'products',           label: 'Producten (Affiliate)' },
      { id: 'ec_products',        label: 'Productbeheer (EC)' },
      { id: 'installers',         label: 'Installateurs' },
      { id: 'device_hub',         label: 'Device Hub' },
      { id: 'device_onboarding',  label: 'Device Onboarding' },
      { id: 'device_discovery',   label: 'Device Discovery',     advanced: true },
      { id: 'device_intelligence',label: 'Device Intelligence',  advanced: true, requiresFlag: 'dia_admin_enabled' },
      { id: 'drivers',            label: 'Drivers',              advanced: true },
      { id: 'components',         label: 'Components',           advanced: true },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing',
    Icon: Target,
    description: 'Ads, agents, campagnes, analytics, rapporten.',
    tabs: [
      { id: 'ads',                  label: 'Ads basisview' },
      { id: 'ads_v2',               label: 'Ads V2 Rebuild',         advanced: true },
      { id: 'ads_control_agent',    label: 'Ads Control Agent',      advanced: true },
      { id: 'ads_automation_agent', label: 'Ads Automation Agent',   advanced: true },
      { id: 'affiliate_agent',      label: 'Affiliate Agent',        advanced: true },
      { id: 'marketing',            label: 'Campagnes' },
      { id: 'analytics',            label: 'Analytics' },
      { id: 'reports',              label: 'Rapporten' },
    ],
  },
  {
    id: 'mail',
    label: 'Mail',
    Icon: Send,
    description: 'Outreach pool, providers, e-mail enrichment.',
    tabs: [
      { id: 'outreach_pool',        label: 'Outreach Pool' },
      { id: 'providers',            label: 'Providers' },
      { id: 'provider_enrichment',  label: 'Provider Enrichment',    advanced: true },
    ],
  },
  {
    id: 'instellingen',
    label: 'Instellingen',
    Icon: Cog,
    description: 'Simulaties, veiligheidschecks, geavanceerde tools.',
    tabs: [
      { id: 'homey_simulation', label: 'Homey Simulation',       advanced: true },
      { id: 'homey_phase3',     label: 'Homey Phase 3 Safety',   advanced: true },
    ],
  },
];

/** Map tabId → groupId (memoized). */
export function getGroupIdForTab(tabId) {
  for (const g of MENU_GROUPS) {
    if (g.tabs.some((t) => t.id === tabId)) return g.id;
  }
  return 'acties';
}

/* --------------------------- Desktop nav ----------------------------- */
function GroupPill({ group, active, onClick }) {
  const { Icon } = group;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`admin-group-${group.id}`}
      className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border whitespace-nowrap transition-colors ${
        active
          ? 'bg-emerald-500/15 text-emerald-200 border-emerald-400/40 font-semibold'
          : 'bg-white/5 text-white/75 border-white/10 hover:bg-white/10 hover:text-white'
      }`}
    >
      <Icon className="w-4 h-4" />
      {group.label}
    </button>
  );
}

function SubTabPill({ tab, active, onClick, available, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!available}
      data-testid={`admin-subtab-${tab.id}`}
      className={`text-xs px-2.5 py-1.5 rounded-md border transition-colors whitespace-nowrap ${
        active
          ? 'bg-emerald-500/20 text-emerald-200 border-emerald-400/50 font-semibold'
          : available
            ? 'bg-white/5 text-white/75 border-white/10 hover:bg-white/10 hover:text-white'
            : 'bg-white/3 text-white/30 border-white/5 cursor-not-allowed'
      }`}
    >
      {tab.label}
      {badge != null && (
        <span className="ml-1 text-[9px] bg-amber-100 text-amber-700 rounded-full px-1.5 py-0.5 font-semibold">{badge}</span>
      )}
    </button>
  );
}

export function AdminGroupNavDesktop({ activeTab, onChange, visibleTabIds = [], badges = {} }) {
  const activeGroupId = getGroupIdForTab(activeTab);
  const [openGroupId, setOpenGroupId] = useState(activeGroupId);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const currentGroup = MENU_GROUPS.find((g) => g.id === openGroupId) || MENU_GROUPS[0];
  const childTabs = useMemo(
    () => currentGroup.tabs.filter((t) =>
      visibleTabIds.includes(t.id) && (!t.advanced || showAdvanced)
    ),
    [currentGroup, visibleTabIds, showAdvanced]
  );

  const onGroupClick = (gid) => {
    setOpenGroupId(gid);
    const g = MENU_GROUPS.find((x) => x.id === gid);
    if (!g) return;
    // Auto-select eerste beschikbare niet-advanced tab in groep.
    const first = g.tabs.find((t) =>
      visibleTabIds.includes(t.id) && !t.advanced
    ) || g.tabs.find((t) => visibleTabIds.includes(t.id));
    if (first) onChange(first.id);
  };

  return (
    <div className="hidden md:block mb-6" data-testid="admin-nav-desktop">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="admin-groups">
          {MENU_GROUPS.map((g) => (
            <GroupPill key={g.id} group={g} active={openGroupId === g.id} onClick={() => onGroupClick(g.id)} />
          ))}
        </div>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          data-testid="admin-toggle-advanced"
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2.5 py-1.5 rounded-md border border-white/15 text-white/65 hover:text-emerald-300 hover:border-emerald-400/30 transition-colors"
        >
          <Sparkles className="w-3 h-3" />
          {showAdvanced ? 'Verberg geavanceerd' : 'Geavanceerd'}
        </button>
      </div>
      <div className="rounded-md border border-white/10 bg-white/3 p-2.5">
        <p className="text-[11px] text-white/55 mb-2 pl-1">
          {currentGroup.description}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {childTabs.length === 0 && (
            <span className="text-[11px] text-white/45 italic px-1">Geen items in deze groep voor jouw rol.</span>
          )}
          {childTabs.map((t) => (
            <SubTabPill
              key={t.id}
              tab={t}
              active={activeTab === t.id}
              available={visibleTabIds.includes(t.id)}
              badge={badges[t.id]}
              onClick={() => onChange(t.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* --------------------------- Mobile nav ----------------------------- */
function MobileGroupCard({ group, isOpen, onToggle, activeTab, onChange, visibleTabIds, badges, showAdvanced }) {
  const { Icon } = group;
  const child = group.tabs.filter((t) =>
    visibleTabIds.includes(t.id) && (!t.advanced || showAdvanced)
  );
  return (
    <div
      className="rounded-xl border border-white/10 bg-white/3 overflow-hidden"
      data-testid={`admin-group-card-${group.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-3 text-left"
        data-testid={`admin-group-card-${group.id}-toggle`}
      >
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 shrink-0">
          <Icon className="w-4 h-4" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-white" style={{ fontFamily: 'Outfit' }}>{group.label}</span>
          <span className="block text-[11px] text-white/55 leading-tight mt-0.5">{group.description}</span>
        </span>
        {isOpen
          ? <ChevronDown className="w-4 h-4 text-white/55" />
          : <ChevronRight className="w-4 h-4 text-white/55" />}
      </button>
      {isOpen && (
        <div className="px-3 pb-3 grid grid-cols-1 gap-1.5">
          {child.length === 0 && (
            <span className="text-[11px] text-white/45 italic px-1 py-2">
              Geen items in deze groep voor jouw rol.
            </span>
          )}
          {child.map((t) => {
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => onChange(t.id)}
                data-testid={`admin-mobile-tab-${t.id}`}
                className={`w-full text-left px-3 py-2 rounded-md border text-sm transition-colors ${
                  active
                    ? 'bg-emerald-500/15 text-emerald-200 border-emerald-400/40 font-semibold'
                    : 'bg-white/5 text-white/85 border-white/10 hover:bg-white/10'
                }`}
              >
                {t.label}
                {badges[t.id] != null && (
                  <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 rounded-full px-1.5 py-0.5 font-semibold">{badges[t.id]}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AdminGroupNavMobile({ activeTab, onChange, visibleTabIds = [], badges = {} }) {
  const activeGroupId = getGroupIdForTab(activeTab);
  const [openId, setOpenId] = useState(activeGroupId);
  const [showAdvanced, setShowAdvanced] = useState(false);
  return (
    <div className="md:hidden mb-5 space-y-2" data-testid="admin-nav-mobile">
      <div className="flex items-center justify-between gap-2 px-1">
        <span className="text-[11px] uppercase tracking-wider text-white/55 font-medium">Owner cockpit</span>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          data-testid="admin-mobile-toggle-advanced"
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-white/15 text-white/65"
        >
          {showAdvanced ? 'Verberg geavanceerd' : 'Geavanceerd'}
        </button>
      </div>
      {MENU_GROUPS.map((g) => (
        <MobileGroupCard
          key={g.id}
          group={g}
          isOpen={openId === g.id}
          onToggle={() => setOpenId(openId === g.id ? null : g.id)}
          activeTab={activeTab}
          onChange={(tabId) => { onChange(tabId); }}
          visibleTabIds={visibleTabIds}
          badges={badges}
          showAdvanced={showAdvanced}
        />
      ))}
    </div>
  );
}
